import { Extension } from '@tiptap/core';
import { DEFAULT_TIPTAP_EXTENSION_PRIORITY, TiptapNodeType } from '../tiptapTypes';
import { EMPTY_NODE_SIZE } from '../utils/isTiptapEditorEmpty';
import { TextSelection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        wrappingBlockCleaner: {
            clearWrappingBlock: () => ReturnType;
        };
    }
}

const CLEARED_NODE_TYPES = new Set<string>([TiptapNodeType.blockquote, TiptapNodeType.callout]);

/**
 * If "return" / "enter" is pressed at the start of a wrapping block (e.g. a blockquote or callout),
 * this extension will create a new paragraph outside of the wrapping block, before the wrapping block,
 * and place the cursor in that paragraph.
 */
export const WrappingBlockClearer = Extension.create({
    // Run before other extensions
    priority: DEFAULT_TIPTAP_EXTENSION_PRIORITY + 10,

    name: 'wrappingBlockCleaner',

    addCommands() {
        return {
            clearWrappingBlock:
                () =>
                ({ tr, dispatch, commands, ...rest }) => {
                    const { selection } = tr;
                    const { empty, $from } = selection;

                    if (!empty) return false;

                    // Check if the current position is at the start of a wrapping block
                    if ($from.parentOffset !== 0) return false;

                    // Check if the parent node is a wrapping block
                    const parent = $from.node($from.depth - 1);

                    if (!parent) return false;
                    if (!CLEARED_NODE_TYPES.has(parent.type.name)) return false;

                    const paragraphNode = $from.parent;
                    if (!paragraphNode) return false;
                    if (paragraphNode.nodeSize === EMPTY_NODE_SIZE) return false;

                    // Ensure it's the first child of the parent
                    if ($from.index($from.depth - 1) !== 0) return false;

                    if (!dispatch) return true;

                    const insertionPos = $from.before($from.depth - 1);

                    // Create a new paragraph before the wrapping block
                    const newParagraph = tr.doc.type.schema.nodes.paragraph.create();
                    let updatedTr = tr.insert(insertionPos, newParagraph);

                    // Move the selection to the new paragraph
                    const newSelection = TextSelection.create(updatedTr.doc, insertionPos, insertionPos);
                    updatedTr = updatedTr.setSelection(newSelection);

                    dispatch(updatedTr);

                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Enter: () => this.editor.commands.clearWrappingBlock(),
        };
    },
});
